import { css } from '@emotion/react';
import { type ReactNode, type SyntheticEvent, useEffect, useId, useLayoutEffect, useRef } from 'react';

import { LAYOUT2 } from '@/Client/Styles';
import { ModalLayout } from '@kaiizen/kai-shiki/react';

const GALLERY_PHOTO_MODAL_LAYOUT_STYLE = css`
  label: GALLERY_PHOTO_MODAL_LAYOUT_STYLE;

  > div {
    grid-template-columns: minmax(0, 1fr) !important;
    width: calc(100vw - ${LAYOUT2.spacing10}px) !important;
    height: calc(100vh - ${LAYOUT2.spacing10}px) !important;
    max-width: none !important;
    max-height: none !important;
  }
`;

const FOCUSABLE_ELEMENT_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  'details > summary:first-of-type',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

const stopViewerEventPropagation = (event: SyntheticEvent) => event.stopPropagation();

/** Finds where focus must move to remain inside the dialog. */
const getContainedFocusTarget = (dialog: HTMLElement, shiftKey: boolean) => {
  const focusableElements = [...dialog.querySelectorAll<HTMLElement>('*')].filter(
    element =>
      element.matches(FOCUSABLE_ELEMENT_SELECTOR)
      && element.tabIndex >= 0
      && !element.matches(':disabled')
      && !element.closest('[hidden], [inert], [aria-hidden="true"]'),
  );
  const firstElement = focusableElements[0] ?? dialog;
  const lastElement = focusableElements[focusableElements.length - 1] ?? dialog;
  const activeElement = document.activeElement;

  if (!dialog.contains(activeElement)) return firstElement;
  if (!focusableElements.includes(activeElement as HTMLElement)) {
    return shiftKey ? lastElement : firstElement;
  }
  if (shiftKey && activeElement === firstElement) return lastElement;
  if (!shiftKey && activeElement === lastElement) return firstElement;
};

/** Keeps Tab navigation within the dialog without assuming viewer controls. */
const containDialogFocus = (event: KeyboardEvent, dialog: HTMLElement) => {
  if (event.key !== 'Tab') return;
  const target = getContainedFocusTarget(dialog, event.shiftKey);
  if (!target) return;
  event.preventDefault();
  target.focus();
};

/** Displays an enlarged Gallery photo without unmounting the Gallery workspace. */
export const GalleryPhotoModal = ({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) => {
  const dialogId = useId();
  const originatingElement = useRef(
    typeof document === 'undefined' || !(document.activeElement instanceof HTMLElement)
      ? undefined
      : document.activeElement,
  );

  useLayoutEffect(() => {
    const focusReturnTarget = originatingElement.current;
    document.getElementById(dialogId)?.focus();
    return () => {
      if (focusReturnTarget?.isConnected) {
        focusReturnTarget.focus();
      }
    };
  }, [dialogId]);

  useEffect(() => {
    const containFocusOrCloseOnEscape = (event: KeyboardEvent) => {
      const dialog = document.getElementById(dialogId);
      if (dialog) containDialogFocus(event, dialog);
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', containFocusOrCloseOnEscape, true);
    return () => window.removeEventListener('keydown', containFocusOrCloseOnEscape, true);
  }, [dialogId, onClose]);

  return (
    <ModalLayout
      id={dialogId}
      role='dialog'
      aria-modal='true'
      aria-label='Enlarged gallery photo'
      tabIndex={-1}
      css={GALLERY_PHOTO_MODAL_LAYOUT_STYLE}
      mediaPane={
        <div
          onClick={stopViewerEventPropagation}
          onMouseDown={stopViewerEventPropagation}
        >
          {children}
        </div>
      }
      detailPane={null}
      onClick={onClose}
    />
  );
};
